import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canAccessPage, firstAllowedPath } from '../utils/pages';
import AuthLoadingScreen from './AuthLoadingScreen';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  /** A key from utils/pages.ts's PAGE_GROUPS — every grantable route, Settings included. */
  page?: string;
  /** Owner-only routes (Chart of Accounts, Journal, Ledger, Trial Balance). */
  ownerOnly?: boolean;
}

export default function ProtectedRoute({ children, page, ownerOnly }: ProtectedRouteProps) {
  const { user, isLoading, isAdmin } = useAuth();

  if (isLoading) return <AuthLoadingScreen />;

  if (isAdmin) return <Navigate to="/admin/panel" replace />;
  if (!user) return <Navigate to="/login" replace />;

  if (ownerOnly && user.role !== 'owner') {
    const fallback = firstAllowedPath(user);
    return fallback ? <Navigate to={fallback} replace /> : <NoAccessScreen />;
  }

  if (page && !canAccessPage(user, page)) {
    const fallback = firstAllowedPath(user);
    return fallback ? <Navigate to={fallback} replace /> : <NoAccessScreen />;
  }

  return <>{children}</>;
}

// A brand-new staff login with nothing granted yet (or everything later
// revoked) has no page to redirect to — show a plain message instead of
// looping, with just a way out.
function NoAccessScreen() {
  const { logout } = useAuth();
  return (
    <div className="loading-page" style={{ flexDirection: 'column', gap: 12, textAlign: 'center' }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)' }}>No pages assigned yet</div>
      <div style={{ fontSize: 13, color: 'var(--text-3)', maxWidth: 320 }}>
        Your account hasn't been given access to any pages yet — contact your business owner.
      </div>
      <button className="btn btn-secondary" style={{ width: 'auto', marginTop: 8 }} onClick={logout}>
        Sign Out
      </button>
    </div>
  );
}
